/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module   public/js/utils/taskMeta.js
 * @summary  Front-end helper to build / parse Topsail step-metadata tags.
 *
 * Format stored in CRM task body
 * ─────────────────────────────────────────────────────────────────────────────
 *   topsail:{"idx":2,"tpl":"Bump","prev":"Intro","prevName":"Intro","type":"EMAIL","data":null,"name":"Intro Call"}
 *
 * • The prefix **topsail:** makes the blob easy to detect.
 * • Any text that follows the first balanced { … } (signatures, HTML, etc.)
 *   is safely ignored by the parser.
 *
 * Helper API
 * ─────────────────────────────────────────────────────────────────────────────
 *   makeMetaComment(meta)  → string   // serialize → tag
 *   parseMeta(body)        → object   // deserialize or null
 *
 * @author    TopSail AI
 * @version   2.1.0   (plain-text only, balanced-brace scan)
 * @since     2025-05-12
 * ─────────────────────────────────────────────────────────────────────────────
 */

'use strict';

/* ── serialize ──────────────────────────────────────────────────────────── */
export function makeMetaComment(meta) {
  return `topsail:${JSON.stringify(meta)}`;
}

/**
 * Normalize call-script metadata into complete label/template pairs.
 *
 * @param {Array<{ label?: string, templateId?: string, template?: string }>|null|undefined} callScripts
 * @returns {Array<{ label: string, templateId: string }>}
 */
export function normaliseCallScripts(callScripts) {
  const entries = Array.isArray(callScripts) ? callScripts : [];
  return entries.reduce((acc, entry) => {
    const label = String(entry?.label ?? '').trim();
    const templateId = String(
      entry?.templateId ?? entry?.template ?? ''
    ).trim();
    if (!label || !templateId) return acc;
    acc.push({ label, templateId });
    return acc;
  }, []);
}

/**
 * Normalize template identifiers into a trimmed, deduplicated array.
 *
 * @param {string[]|string|null|undefined} templateIds
 * @returns {string[]}
 */
export function normaliseTemplateIdentifiers(templateIds) {
  const values = Array.isArray(templateIds) ? templateIds : [templateIds];
  return values.reduce((acc, entry) => {
    const value = String(entry ?? '').trim();
    if (!value || acc.includes(value)) return acc;
    acc.push(value);
    return acc;
  }, []);
}

/* ── parse ──────────────────────────────────────────────────────────────── */
export function parseMeta(body = '') {
  const TAG = 'topsail:';
  const idx = body.indexOf(TAG);
  if (idx === -1) return null;

  const src = body.slice(idx + TAG.length); // after the tag

  // Walk to first balanced { … }
  let depth = 0,
    start = -1,
    end = -1;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0) {
        end = i + 1;
        break;
      }
    }
  }
  if (start === -1 || end === -1) return null;

  const jsonStr = src.slice(start, end);
  let obj;
  try {
    obj = JSON.parse(jsonStr);
  } catch (err) {
    console.warn('[taskMeta] Bad JSON:', err, jsonStr);
    return null;
  }

  // Defaults for legacy blobs
  return {
    idx: obj.idx ?? null,
    tpl: obj.tpl ?? null,
    goEmailTpl: normaliseTemplateIdentifiers(obj.goEmailTpl)[0] ?? null,
    prev: obj.prev ?? null,
    prevName: obj.prevName ?? null,
    type: obj.type ?? null,
    data: obj.data ?? null,
    thread: obj.thread ?? false,
    name: obj.name ?? null,
    templates: normaliseTemplateIdentifiers(obj.templates),
    goEmailTemplates: normaliseTemplateIdentifiers(obj.goEmailTemplates),
    mergeVars: obj.mergeVars ?? {},
    callScripts: normaliseCallScripts(obj.callScripts),
  };
}

/* legacy alias (older imports still call this) */
export const parseMetaComment = parseMeta;
